import { useEffect, useRef, type ReactNode } from 'react';

/**
 * La fenêtre de la console — chantier « manuel vivant », Φ-MV-5 (MV-TUT-13).
 * La console n'en avait pas : ses saisies vivaient dans la page. F-11 demande
 * l'inverse — « une saisie courte se fait en fenêtre » (MV-UX-02) : l'objectif
 * d'un élève, la note d'une séance, une confirmation.
 *
 * ─── `<dialog>` natif, ouvert par `showModal()` ─────────────────────────────
 *
 * Le navigateur fait alors ce qu'une fenêtre faite à la main rate toujours : il
 * PIÈGE le focus dans la fenêtre, rend le reste de la page inerte, ferme sur
 * Échap et REND le focus au bouton d'où l'on vient. Rien de cela n'est écrit
 * ici — donc rien de cela ne peut diverger.
 *
 * Trois façons de fermer, toutes par `surFermeture` : Échap (`cancel`), un clic
 * sur le voile (la cible est alors le `<dialog>` lui-même, la carte le remplit),
 * et la croix « Fermer ». Le parent démonte la fenêtre ; elle ne se ferme pas
 * seule — une saisie en cours n'est jamais perdue par un état local.
 *
 * Une fenêtre a SON bouton plein (« Enregistrer ») : c'est une surface à part,
 * la règle « un seul bouton plein par écran » se compte dans la fenêtre.
 */
export interface FenetreProps {
  readonly titre: string;
  /** Une ligne sous le titre : pour qui, ou qui lira ce qui s'écrit ici. */
  readonly sous?: ReactNode;
  /** Variante large : un formulaire à plusieurs sections (l'objectif). */
  readonly large?: boolean;
  readonly surFermeture: () => void;
  /** Le pied : « Annuler » à gauche du bouton plein. */
  readonly actions?: ReactNode;
  readonly testId?: string;
  readonly children?: ReactNode;
}

export function Fenetre({
  titre,
  sous,
  large = false,
  surFermeture,
  actions,
  testId,
  children,
}: FenetreProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);

  return (
    <dialog
      ref={ref}
      className={large ? 'fenetre fenetre--large' : 'fenetre'}
      aria-label={titre}
      data-testid={testId}
      onCancel={(e) => {
        e.preventDefault();
        surFermeture();
      }}
      onClick={(e) => {
        if (e.target === ref.current) surFermeture();
      }}
    >
      <div className="fenetre__carte">
        <header className="fenetre__tete">
          <div>
            <h2 className="fenetre__titre">{titre}</h2>
            {sous !== undefined && sous !== null ? <p className="card-sub">{sous}</p> : null}
          </div>
          <button
            type="button"
            className="fenetre__fermer"
            aria-label="Fermer"
            onClick={surFermeture}
          >
            <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
              <path
                d="M3.5 3.5 12.5 12.5 M12.5 3.5 3.5 12.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>
        {children !== undefined && children !== null ? (
          <div className="fenetre__corps">{children}</div>
        ) : null}
        {actions !== undefined && actions !== null ? (
          <footer className="fenetre__actions">{actions}</footer>
        ) : null}
      </div>
    </dialog>
  );
}
